import React, { useState } from "react";
import { Paperclip, FileText } from "lucide-react";

export default function EvidenceNotesBlock({
  fieldKey,
  evidence,
  setEvidence,
  notes,
  setNotes,
  uploadDraftFile,
  currentDraftId,
}) {
  const [showEvidence, setShowEvidence] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleEvidenceUpload = async (file) => {
    if (!file || !uploadDraftFile || !currentDraftId) return;

    try {
      setUploading(true);
      const url = await uploadDraftFile(file, currentDraftId, fieldKey + '_evidence');

      setEvidence((prev) => ({
        ...prev,
        [fieldKey]: {
          _fileType: 'uploaded',
          url,
          name: file.name,
          type: file.type,
          size: file.size
        },
      }));
    } catch (error) {
      console.error('Failed to upload evidence:', error);
      alert('Failed to upload evidence: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mt-4">
      {/* Show uploaded evidence preview before collapsible section */}
      {evidence[fieldKey]?._fileType === 'uploaded' && (
        <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
          <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Paperclip size={16} />
            Evidence Uploaded
          </div>
          {evidence[fieldKey]?.type?.startsWith("image") ? (
            <img
              src={evidence[fieldKey].url}
              alt="Evidence"
              className="max-w-xs rounded border border-gray-300"
            />
          ) : (
            <div className="flex items-center gap-2 text-blue-600">
              <FileText size={16} />
              <a
                href={evidence[fieldKey].url}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                {evidence[fieldKey].name}
              </a>
            </div>
          )}
          <button
            type="button"
            className="mt-2 text-sm text-red-600 hover:text-red-800"
            onClick={() => {
              setEvidence(prev => {
                const c = { ...prev };
                delete c[fieldKey];
                return c;
              });
            }}
          >
            Remove evidence
          </button>
        </div>
      )}

      {/* ---- BUTTON ROW ---- */}
      <div className="flex items-center gap-3 mb-3">
        {/* Upload Evidence Button */}
        <button
          type="button"
          onClick={() => setShowEvidence((v) => !v)}
          className="px-4 py-2 bg-orange-500 text-white rounded-md shadow-sm hover:bg-orange-600 transition flex items-center gap-2"
        >
          {evidence[fieldKey]?._fileType === 'uploaded' ? 'Replace Evidence' : 'Upload Evidence'}
        </button>

        {/* Notes Toggle Button */}
        <button
          type="button"
          onClick={() => setShowNotes((v) => !v)}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
        >
          {showNotes ? "Hide notes" : "Add notes"}
        </button>
      </div>

      {/* ---- COLLAPSIBLE EVIDENCE PANEL ---- */}
      {showEvidence && (
        <div className="border border-gray-200 rounded-lg bg-gray-50 p-4 mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Paperclip size={16} />
            Upload Evidence
          </label>

          <input
            type="file"
            className="block w-full text-sm border border-gray-300 rounded-md p-2 bg-white"
            disabled={uploading}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleEvidenceUpload(file);
              }
            }}
          />

          {uploading && (
            <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Uploading...
            </div>
          )}

          {/* UPLOADED FILE PREVIEW */}
          {evidence[fieldKey]?._fileType === 'uploaded' && (
            <div className="mt-3 space-y-2">
              {evidence[fieldKey]?.type?.startsWith("image") ? (
                <img
                  src={evidence[fieldKey].url}
                  alt="Evidence"
                  className="max-w-xs rounded border border-gray-300"
                />
              ) : (
                <div className="flex items-center gap-2 text-blue-600">
                  <FileText size={16} />
                  <a
                    href={evidence[fieldKey].url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                  >
                    {evidence[fieldKey].name}
                  </a>
                </div>
              )}
              <button
                type="button"
                className="text-sm text-red-600 hover:text-red-800"
                onClick={() => {
                  setEvidence(prev => {
                    const c = { ...prev };
                    delete c[fieldKey];
                    return c;
                  });
                }}
              >
                Remove evidence
              </button>
            </div>
          )}
        </div>
      )}

      {/* ---- COLLAPSIBLE NOTES PANEL ---- */}
      {showNotes && (
        <div className="border border-gray-200 rounded-lg bg-gray-50 p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            📝 Notes
          </label>

          <textarea
            rows={3}
            className="w-full p-3 border-2 border-orange-300 rounded-lg bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400"
            placeholder="Add any notes..."
            value={notes[fieldKey] || ""}
            onChange={(e) =>
              setNotes((prev) => ({
                ...prev,
                [fieldKey]: e.target.value,
              }))
            }
          />
        </div>
      )}
    </div>
  );
}